/**
 * PDP Media Gallery — hover variant-image preview (desktop). OWNED.
 *
 * Color Echo v1.8.0 — canonical registry: ColorEcho-Install.md §2 — do not
 * edit without bumping the version everywhere (grep "Color Echo v").
 *
 * Loaded by snippets/pdp-media-gallery-content.liquid ONLY when the gallery
 * block setting hover_variant_image is on (off = zero requests, zero
 * listeners' work beyond two cheap closest() misses). On desktop
 * (hover: hover, pointer: fine), hovering a color swatch in the sibling
 * variant picker temporarily shows that variant's featured image over the
 * main gallery viewport; the pointer leaving reverts to the true selection.
 * Complements the Color Echo color hover preview (color-echo.js) but is
 * fully independent of color_echo_enabled.
 *
 * Mechanics (v1.6.0 — structural clone): the overlay is no longer a bare
 * <img> pinned to slideshow-container. It is a DIV that clones the active
 * slide's own wrapper structure — the slide's .product-media-container
 * class list (media-fit / constrain / zoomable configuration), its inline
 * custom properties (--product-media-fit, --media-preview-ratio for the
 * 'adapt' machinery), the optional .product-media-constraint-wrapper, and a
 * .product-media > img.product-media__image pair — so the THEME'S OWN CSS
 * sizes the preview with exactly the geometry the real slide image has
 * (same aspect-ratio, object-fit, object-position, max-width/alignment;
 * the v1.5.0 scale mismatch came from the bare img resolving
 * object-fit/position on the container where the slide-level classes and
 * custom properties never applied). Only position/inset/z-index/
 * pointer-events ride inline (they must outrank the theme's positional
 * rules). Color Echo gallery modes keep working: multiply/band key on the
 * .product-media-container--image CLASS (which the clone carries), and the
 * mat 12px card selectors in color-echo-styles.liquid explicitly include
 * .pdp-gallery-hover-preview-overlay.
 * Never an src swap on the real slides, so there is zero CLS and no fight
 * with the in-place variant swap (pdp-gallery-variant-media.js); a
 * committed selection (delegated change) removes the preview immediately
 * and the real swap machinery proceeds untouched. srcset/URL building is
 * imported from pdp-gallery-variant-media.js (same CDN-width approach as
 * the variant swap). Data source: a per-block JSON map (option value id →
 * featured image) emitted at render time by pdp-media-gallery-content.liquid.
 *
 * Transitions (v1.7.0 — instant interruptible ramped crossfades; the
 * v1.6.0 hover-intent delay is REMOVED by owner ruling — it read as lag):
 * - INSTANT START: the fade begins the moment the pointer enters a swatch
 *   (zero delay; the image load also starts instantly, as before).
 * - RAMPED ONSET (the suppression mechanism): the opacity-IN curve has a
 *   deliberately shallow start (ease-in-out, see FEELS), so a fast sweep
 *   across the swatch row — which retargets the CSS transition continuously,
 *   always FROM the current opacity — only ever lets each intermediate image
 *   reach faint opacity: a fluid glimmer that still reads as instant visual
 *   change. Settling on one swatch lets the ramp complete to 100% with a
 *   decelerating landing. This is the standard rapid-retarget hover model
 *   (Material motion / iOS dissolve): native CSS transition interruption
 *   replaces the old suppression timer.
 * - CROSSFADE between successive previews: at most TWO pooled overlay
 *   elements; the incoming one fades in over the outgoing one (both carry
 *   an opaque background), then the stale one is dropped — no flash.
 * - POINTER-OUT: the inverse — a quick shallow release (ease-out curve,
 *   slightly shorter duration): responsive but not abrupt; a re-hover
 *   before removal cancels the schedule and fades back in from wherever
 *   the opacity currently is.
 * - READINESS: a preview is revealed only once its image has pixels
 *   (complete/load-gated) — no spinner, no background flash. With zero
 *   delay this matters more, so a WARM-UP prefetches every preview image
 *   for the gallery (low priority, nearest-first) at the FIRST pointerover
 *   anywhere on the swatch row — never at page load; once per gallery. The
 *   warm requests use the exact same src/srcset/sizes the overlay <img>
 *   uses (both built by previewSources(), so they match by construction).
 * Feel presets (hover_preview_feel), ALL zero-delay — see FEELS below.
 * All fades are plain CSS opacity transitions (static stylesheet in
 * pdp-media-gallery-content.liquid); prefers-reduced-motion snaps them.
 *
 * Morph survival: all listeners are delegated to document and every node
 * (label, gallery, map, container, overlay) is re-resolved per event; a
 * morph destroying an overlay mid-timer is caught by isConnected guards and
 * the next hover rebuilds. Zakeke coexistence: pointer/change events only —
 * NO click listeners. No requestAnimationFrame anywhere (hidden-tab freeze
 * rule); removal scheduling is setTimeout, fades are CSS transitions.
 */

// Same-directory relative specifier — the exact resolution the theme already
// relies on as the dynamic-import fallback in pdp-gallery-media-gallery.js.
import { imageUrl, srcsetFor, SLIDE_WIDTHS } from './pdp-gallery-variant-media.js';

const HOVER_CAPABLE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const MAP_SELECTOR = 'script[type="application/json"][data-pdp-hover-preview]';
const LABEL_SELECTOR = 'label.variant-option__button-label--has-swatch';
const OVERLAY_CLASS = 'pdp-gallery-hover-preview-overlay';
const VISIBLE_CLASS = 'is-visible';

/**
 * Feel presets — the ONE control-surface knob (hover_preview_feel). All
 * presets start INSTANTLY (zero delay — owner ruling, v1.7.0); they differ
 * only in ramp depth and duration.
 *
 * Curve rationale:
 * - IN is ease-in-out, NOT the pure ease-in the ramp brief suggested first:
 *   the shallow onset is what suppresses fast sweeps (at soft, a 50ms dwell
 *   peaks near 5% opacity, 80ms near 15%, 120ms near 40% — see the header),
 *   but a pure ease-in (e.g. cubic-bezier(0.32, 0, 0.67, 0)) ARRIVES at
 *   full velocity, so completion reads as a snap. The symmetric S-curve
 *   keeps the ramped onset AND a decelerating, confident landing — the
 *   Material-motion shape for content that must settle calmly.
 *   soft uses cubic-bezier(0.55, 0, 0.45, 1) (≈ easeInOutQuad/cubic blend);
 *   relaxed deepens the ramp with cubic-bezier(0.65, 0, 0.35, 1)
 *   (easeInOutCubic — a longer near-zero head); instant uses the plain
 *   'ease' keyword because at 120ms a ramp is imperceptible and the preset's
 *   promise is a strong immediate flash per swatch.
 * - OUT is easeOutCubic cubic-bezier(0.33, 1, 0.68, 1) everywhere: an
 *   immediate visible release (responsive) with a soft tail (not abrupt),
 *   slightly shorter than IN so reverting always feels lighter than arriving.
 * @type {Record<string, { in: number, out: number, easeIn: string, easeOut: string }>}
 */
const FEELS = {
  instant: { in: 120, out: 100, easeIn: 'ease', easeOut: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  soft: { in: 260, out: 180, easeIn: 'cubic-bezier(0.55, 0, 0.45, 1)', easeOut: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  relaxed: { in: 420, out: 260, easeIn: 'cubic-bezier(0.65, 0, 0.35, 1)', easeOut: 'cubic-bezier(0.33, 1, 0.68, 1)' },
};

/** @typedef {{ url: string, width: number, height: number, alt: string|null }} PreviewImage */
/** @typedef {{ in: number, out: number, easeIn: string, easeOut: string }} Feel */

/** Per-script-tag parsed map cache (WeakMap: a morph replacing the tag re-parses naturally). */
const parsedMaps = new WeakMap();

/** Galleries whose preview images have been warmed (WeakSet: a morph-replaced gallery re-warms naturally). */
const warmedGalleries = new WeakSet();

/** In-flight warm-up Image elements, held so garbage collection can never abort a warm request. */
const warmingImages = new Set();

/** The src the current hover intends to show (null = no active hover). */
let activeSrc = null;

/**
 * @param {EventTarget | null} target - The event target.
 * @returns {Element | null} The enclosing swatch label, if any (same hook as color-echo.js).
 */
function hoverLabel(target) {
  return target instanceof Element ? target.closest(LABEL_SELECTOR) : null;
}

/**
 * Resolves the hover-preview JSON map that shares a section with the label.
 * Re-resolved per event: the Section Rendering API can replace the gallery.
 * @param {Element} label - The hovered swatch label (or any element inside the picker's section).
 * @returns {{ gallery: Element, map: Record<string, PreviewImage>, feel: Feel } | null}
 */
function resolveMap(label) {
  const section = label.closest('section, .shopify-section');
  const script = section?.querySelector(`pdp-media-gallery ${MAP_SELECTOR}`);
  const gallery = script?.closest('pdp-media-gallery');
  if (!script || !gallery || !(script instanceof HTMLElement)) return null;
  let map = parsedMaps.get(script);
  if (!map) {
    try {
      map = JSON.parse(script.textContent ?? '{}');
    } catch {
      map = {};
    }
    parsedMaps.set(script, map);
  }
  const feelKey = script.dataset.feel ?? '';
  const feel = Object.hasOwn(FEELS, feelKey) ? FEELS[feelKey] : FEELS.soft;
  return { gallery, map, feel };
}

/**
 * The single source of the preview image's request triple. Used by BOTH the
 * overlay <img> and the warm-up prefetch, so the warm-up necessarily hits
 * the exact cache entries the overlay will ask for (src, srcset, and the
 * sizes attribute all match by construction — the browser's srcset
 * selection is deterministic for a given viewport/DPR, so identical inputs
 * select the identical candidate URL).
 * @param {Element} gallery - The <pdp-media-gallery> element.
 * @param {PreviewImage} image - The preview image payload.
 * @returns {{ src: string, srcset: string, sizes: string }}
 */
function previewSources(gallery, image) {
  const sizes =
    gallery.querySelector('slideshow-slides img.product-media__image')?.getAttribute('sizes') || '100vw';
  return {
    src: imageUrl(image.url, Math.min(1600, image.width)),
    srcset: srcsetFor(image, SLIDE_WIDTHS),
    sizes,
  };
}

/**
 * Warm-up prefetch (v1.7.0): with zero reveal delay, an un-decoded image at
 * hover time is the one thing that can make the preview late, so the first
 * pointerover anywhere on the swatch row warms EVERY preview image for the
 * gallery — nearest-first from the entered swatch, at low fetch priority
 * (fetchpriority='low' where supported) so a 20-color product's burst never
 * competes with user-blocking work. Never runs at page load; once per
 * gallery element; desktop only (the listeners are HOVER_CAPABLE-gated).
 * @param {Element} row - The swatch row (fieldset) the pointer entered.
 * @param {Element | null} label - The specific swatch label, when the pointer entered one.
 */
function warmGallery(row, label) {
  const resolved = resolveMap(row);
  if (!resolved || warmedGalleries.has(resolved.gallery)) return;
  warmedGalleries.add(resolved.gallery);
  const inputs = [...row.querySelectorAll('input[data-option-value-id]')];
  const hoveredInput = label?.querySelector('input[data-option-value-id]');
  const hoveredIndex = Math.max(0, inputs.findIndex((input) => input === hoveredInput));
  const ordered = inputs
    .map((input, index) => ({ id: /** @type {HTMLElement} */ (input).dataset.optionValueId ?? '', index }))
    .sort((a, b) => Math.abs(a.index - hoveredIndex) - Math.abs(b.index - hoveredIndex));
  for (const { id } of ordered) {
    const image = resolved.map[id];
    if (!image || !image.url) continue;
    const sources = previewSources(resolved.gallery, image);
    const img = new Image();
    if ('fetchPriority' in img) img.fetchPriority = 'low';
    img.decoding = 'async';
    img.sizes = sources.sizes;
    img.srcset = sources.srcset;
    img.src = sources.src;
    warmingImages.add(img);
    const release = () => warmingImages.delete(img);
    img.addEventListener('load', release, { once: true });
    img.addEventListener('error', release, { once: true });
  }
}

/**
 * Cancels a scheduled overlay removal (safe on elements without one).
 * @param {HTMLElement} overlay
 */
function cancelRemove(overlay) {
  if (overlay.dataset.hvpTimer) {
    clearTimeout(Number(overlay.dataset.hvpTimer));
    delete overlay.dataset.hvpTimer;
  }
}

/**
 * Schedules an overlay's removal, replacing any earlier schedule.
 * @param {HTMLElement} overlay
 * @param {number} ms
 */
function scheduleRemove(overlay, ms) {
  cancelRemove(overlay);
  overlay.dataset.hvpTimer = String(
    setTimeout(() => {
      overlay.remove();
    }, ms)
  );
}

/**
 * Reads an overlay's stored fade durations (fallback: the soft preset).
 * @param {HTMLElement} overlay
 * @returns {{ in: number, out: number }}
 */
function overlayFades(overlay) {
  return {
    in: Number(overlay.dataset.fadeInMs) || FEELS.soft.in,
    out: Number(overlay.dataset.fadeOutMs) || FEELS.soft.out,
  };
}

/**
 * Builds a structural clone of the gallery's slide markup around the preview
 * image, so the theme's own CSS gives the overlay EXACTLY the real slide
 * geometry (aspect-ratio, media-fit, object-position, constrain wrapper).
 * @param {Element} gallery - The <pdp-media-gallery> element.
 * @param {PreviewImage} image - The preview image payload.
 * @param {Feel} feel - The feel preset.
 * @returns {HTMLElement | null} The connected overlay, or null when the gallery has no usable slide.
 */
function buildOverlay(gallery, image, feel) {
  const container = gallery.querySelector('slideshow-container');
  if (!container) return null;
  // Template: an existing image slide — its class list and inline custom
  // properties carry the block's whole geometry configuration (same
  // template-slide approach as the in-place variant swap).
  const templateSlide =
    container.querySelector('slideshow-slides > slideshow-slide.product-media-container--image') ||
    container.querySelector('slideshow-slides > slideshow-slide');
  if (!(templateSlide instanceof HTMLElement)) return null;

  const overlay = document.createElement('div');
  const classes = templateSlide.className
    .replace(/\bproduct-media-container--(video|external_video|model)\b/g, 'product-media-container--image')
    .replace(/\bproduct-media-container--tallest\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  overlay.className = `${OVERLAY_CLASS} ${classes}`;
  overlay.setAttribute('aria-hidden', 'true');

  // Inherit the slide's inline custom props (--product-media-fit, grid rows,
  // --media-preview-ratio), then adapt the ratio to the PREVIEW image exactly
  // as the real swap would (aspect_ratio 'adapt' only — fixed ratios come
  // from --gallery-aspect-ratio on the gallery root and need no override).
  overlay.style.cssText = templateSlide.style.cssText;
  overlay.style.removeProperty('view-transition-name');
  const ratio = image.height ? image.width / image.height : 1;
  if (templateSlide.style.getPropertyValue('--media-preview-ratio') !== '') {
    overlay.style.setProperty('--media-preview-ratio', String(ratio));
  }
  // Geometry pin — inline so it outranks the theme's positional rules on
  // .product-media-container. z-index 2 sits above the slides but below the
  // arrows and the 42px overlay circles; pointer-events none keeps slide
  // clicks (zoom) and Zakeke's interceptor untouched. Background, opacity,
  // and the fades live in the static stylesheet
  // (pdp-media-gallery-content.liquid) at LOW specificity, so the theme's
  // and Color Echo's slide background rules (mat card, multiply tint)
  // override the overlay exactly as they do the real slides, and
  // prefers-reduced-motion can snap the fades. The four --hvp-* custom
  // properties carry the feel preset (durations AND easing functions) into
  // the static transition declarations.
  overlay.style.position = 'absolute';
  overlay.style.inset = '0';
  overlay.style.zIndex = '2';
  overlay.style.pointerEvents = 'none';
  overlay.style.setProperty('--hvp-fade-in', `${feel.in}ms`);
  overlay.style.setProperty('--hvp-fade-out', `${feel.out}ms`);
  overlay.style.setProperty('--hvp-ease-in', feel.easeIn);
  overlay.style.setProperty('--hvp-ease-out', feel.easeOut);
  overlay.dataset.fadeInMs = String(feel.in);
  overlay.dataset.fadeOutMs = String(feel.out);

  const media = document.createElement('div');
  media.className = 'product-media';
  media.style.setProperty('--ratio', String(ratio));

  const img = document.createElement('img');
  img.className = 'product-media__image';
  img.alt = '';
  img.decoding = 'async';
  // eager is safe here: the element only ever exists after a deliberate hover.
  img.loading = 'eager';
  img.width = image.width;
  img.height = image.height;
  // Request triple shared with the warm-up prefetch — see previewSources().
  const sources = previewSources(gallery, image);
  img.sizes = sources.sizes;
  img.srcset = sources.srcset;
  img.src = sources.src;
  media.appendChild(img);

  if (templateSlide.querySelector(':scope > .product-media-constraint-wrapper')) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-media-constraint-wrapper';
    wrapper.appendChild(media);
    overlay.appendChild(wrapper);
  } else {
    overlay.appendChild(media);
  }
  container.appendChild(overlay);
  return overlay;
}

/**
 * Returns (creating on demand) the overlay for this preview image and starts
 * its image load immediately. Pool discipline: at most TWO overlay elements
 * ever exist in a container (the visible/outgoing one plus the incoming one).
 * @param {Element} gallery - The <pdp-media-gallery> element.
 * @param {PreviewImage} image - The preview image payload.
 * @param {Feel} feel - The feel preset.
 * @returns {HTMLElement | null}
 */
function ensureOverlay(gallery, image, feel) {
  const container = gallery.querySelector('slideshow-container');
  if (!container) return null;
  const src = previewSources(gallery, image).src;
  const overlays = [...container.querySelectorAll(`:scope > .${OVERLAY_CLASS}`)];
  const existing = overlays.find((el) => el instanceof HTMLElement && el.dataset.previewSrc === src);
  if (existing instanceof HTMLElement) {
    cancelRemove(existing);
    return existing;
  }
  // Prune to at most one survivor before adding the incoming element: drop
  // never-revealed loaders outright, but let an overlay that has SHOWN keep
  // fading (visible or easing out after a pointerout) — snapping it away
  // mid-fade is exactly the flicker this module exists to remove. Its
  // scheduled removal (or the incoming reveal's cleanup) retires it.
  const survivors = [];
  for (const el of overlays) {
    if (el instanceof HTMLElement && el.dataset.revealed) {
      survivors.push(el);
    } else {
      el.remove();
    }
  }
  while (survivors.length > 1) {
    const stale = survivors.shift();
    if (stale) stale.remove();
  }
  const overlay = buildOverlay(gallery, image, feel);
  if (overlay) overlay.dataset.previewSrc = src;
  return overlay;
}

/**
 * Fades the overlay in and settles the pool: overlays stacked ABOVE it fade
 * out (uncovering it), overlays beneath it are dropped once the crossfade
 * has fully covered them.
 * @param {HTMLElement} overlay
 */
function revealOverlay(overlay) {
  if (!overlay.isConnected) return;
  cancelRemove(overlay);
  // Force style resolution so the opacity transition runs even when the
  // element was inserted earlier in this same task (no requestAnimationFrame).
  void overlay.offsetWidth;
  overlay.classList.add(VISIBLE_CLASS);
  overlay.dataset.revealed = 'true';
  const container = overlay.parentElement;
  if (!container) return;
  const fades = overlayFades(overlay);
  // Sibling retirement waits for whichever fade finishes last (the incoming
  // ramp fully covering it, or its own release reaching 0).
  const settle = Math.max(fades.in, fades.out) + 50;
  let above = false;
  for (const sibling of container.querySelectorAll(`:scope > .${OVERLAY_CLASS}`)) {
    if (sibling === overlay) {
      above = true;
      continue;
    }
    if (!(sibling instanceof HTMLElement)) continue;
    if (above) sibling.classList.remove(VISIBLE_CLASS);
    scheduleRemove(sibling, settle);
  }
}

/**
 * Reveals the overlay once its image has pixels: synchronously when cached
 * (the warm-up's normal outcome), otherwise the ramp starts the moment load
 * fires — still gated on the hover being current.
 * @param {HTMLElement} overlay
 */
function queueReveal(overlay) {
  const img = overlay.querySelector('img');
  if (!(img instanceof HTMLImageElement)) return;
  if (img.complete && img.naturalWidth > 0) {
    revealOverlay(overlay);
    return;
  }
  img.addEventListener(
    'load',
    () => {
      if (overlay.dataset.previewSrc === activeSrc) revealOverlay(overlay);
    },
    { once: true }
  );
}

/**
 * Eases every preview out (pointerout / hover of the true selection):
 * revealed overlays release to 0 on the out curve and are removed after the
 * fade; loaders that never showed are dropped immediately. A re-hover before
 * removal cancels the schedule and fades back in from the current opacity.
 */
function hidePreviews() {
  activeSrc = null;
  for (const overlay of document.querySelectorAll(`.${OVERLAY_CLASS}`)) {
    if (!(overlay instanceof HTMLElement)) continue;
    if (overlay.classList.contains(VISIBLE_CLASS)) {
      overlay.classList.remove(VISIBLE_CLASS);
      scheduleRemove(overlay, overlayFades(overlay).out);
    } else {
      overlay.remove();
    }
  }
}

/** Removes every preview overlay on the page instantly (committed selection). */
function clearPreview() {
  activeSrc = null;
  for (const overlay of document.querySelectorAll(`.${OVERLAY_CLASS}`)) {
    overlay.remove();
  }
}

/**
 * @param {PointerEvent} event - The delegated pointerover event.
 */
function handlePointerOver(event) {
  const label = hoverLabel(event.target);
  // Warm-up trigger: the first pointerover anywhere on a swatch ROW (the
  // fieldset containing swatch labels — including the gaps between labels)
  // prefetches the gallery's preview images. Cheap after the first hit: one
  // closest() plus a WeakSet check.
  const row = event.target instanceof Element ? event.target.closest('fieldset') : null;
  if (row && row.querySelector(LABEL_SELECTOR)) warmGallery(row, label);
  if (!label) return;
  // Ignore moves between children of the same label (mirrors color-echo.js).
  if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

  const input = label.querySelector('input[data-option-value-id]');
  if (!(input instanceof HTMLInputElement) || !input.dataset.optionValueId) return;
  // The true selection is already showing — ease any preview out.
  if (input.checked) {
    hidePreviews();
    return;
  }

  const resolved = resolveMap(label);
  const image = resolved?.map[input.dataset.optionValueId];
  if (!resolved || !image || !image.url) {
    // Unmapped value (no featured image): ease any stale preview out rather
    // than showing the previously hovered variant's image.
    hidePreviews();
    return;
  }

  // Instant start (v1.7.0): the load kicks NOW and the ramp starts the
  // moment pixels exist — no intent delay. Sweep suppression is the ramped
  // ease-in onset plus CSS transition retargeting (always from the current
  // opacity), not a timer.
  const overlay = ensureOverlay(resolved.gallery, image, resolved.feel);
  if (!overlay) return;
  activeSrc = overlay.dataset.previewSrc ?? null;
  queueReveal(overlay);
}

/**
 * @param {PointerEvent} event - The delegated pointerout event.
 */
function handlePointerOut(event) {
  const label = hoverLabel(event.target);
  if (!label) return;
  if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;
  hidePreviews();
}

if (HOVER_CAPABLE) {
  document.addEventListener('pointerover', handlePointerOver);
  document.addEventListener('pointerout', handlePointerOut);
  // A committed selection outranks the preview: clear immediately so the real
  // variant-media swap (Section Rendering or pdp-gallery-variant-media.js)
  // is never covered. Delegated change survives picker morphs.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof Element && input.closest('variant-picker')) clearPreview();
  });
}
